import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Calendar, Sparkles, BookOpen, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import AppShell from '@/components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DailyQuestions() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [topic, setTopic] = useState('');

  useEffect(() => {
    fetchQuestions();
  }, [selectedDate]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/daily-questions`, {
        params: { date_str: selectedDate }
      });
      setQuestions(response.data);
    } catch (error) {
      toast.error('Failed to load daily questions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    try {
      setCreating(true);
      await axios.post(`${API}/daily-questions`, {
        topic: topic,
        grade_levels: [9, 10, 11, 12]
      });
      toast.success('Daily questions created successfully!');
      setShowCreate(false);
      setTopic('');
      fetchQuestions();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Questions already exist for today');
      } else {
        toast.error('Failed to create questions');
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header + create button */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Resources</p>
            <h1 className="font-serif-display text-4xl text-[#14213D] tracking-tight">Daily Current Events</h1>
            <p className="text-sm text-[#5A6577] mt-2 max-w-2xl">Generate timely discussion questions tied to today&rsquo;s headlines.</p>
          </div>
          {!showCreate && (
            <Button
              className="btn-primary"
              onClick={() => setShowCreate(true)}
              data-testid="create-daily-questions-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Today&rsquo;s Questions
            </Button>
          )}
        </div>

        {/* Date Selector */}
        <div className="mb-8">
          <Label htmlFor="date-select" className="text-sm font-medium text-[#2D3748] mb-2 block">
            Select Date
          </Label>
          <Input
            id="date-select"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="max-w-xs"
            data-testid="date-selector"
          />
        </div>

        {/* Create Questions Form */}
        {showCreate && (
          <Card className="p-8 mb-8 bg-[#FBF6E8] border-[#D4AF37]/40">
            <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-4">
              Generate Daily Questions
            </h2>
            <p className="text-sm text-[#5A6577] mb-6">
              Enter a current event topic and AI will generate 2-4 discussion questions for students
            </p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="topic">Current Event Topic *</Label>
                <Textarea
                  id="topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., 'Recent Supreme Court decision on environmental policy' or 'International climate summit outcomes'"
                  rows={3}
                  data-testid="topic-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="btn-primary"
                  data-testid="generate-questions-btn"
                >
                  {creating ? 'Generating...' : 'Generate Questions'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreate(false);
                    setTopic('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Display Questions */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent"></div>
            <p className="mt-4 text-[#5A6577]">Loading questions...</p>
          </div>
        ) : questions && questions.questions && questions.questions.length > 0 ? (
          <Card className="p-8" data-testid="questions-display">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-[#FBF6E8] rounded-lg">
                <BookOpen className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-2">
                  Questions for {new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h2>
                {questions.topic && (
                  <p className="text-sm text-[#5A6577]">
                    <strong>Topic:</strong> {questions.topic}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {questions.questions.map((question, index) => (
                <div 
                  key={`q-${index}-${question?.slice(0, 40) || ''}`} 
                  className="border-l-4 border-yellow-500 pl-6 py-3"
                  data-testid={`question-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="font-mono text-sm font-semibold text-[#14213D] mt-1">
                      Q{index + 1}
                    </span>
                    <p className="text-[#14213D] leading-relaxed flex-1">{question}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-[#F9F8F4] rounded-lg">
              <h3 className="font-semibold text-[#14213D] mb-2 text-sm">Discussion Tips</h3>
              <ul className="text-xs text-[#2D3748] space-y-1 list-disc list-inside">
                <li>Encourage students to cite evidence and examples</li>
                <li>Promote respectful dialogue and diverse perspectives</li>
                <li>Connect to historical patterns and themes from curriculum</li>
                <li>Allow time for individual reflection before group discussion</li>
              </ul>
            </div>
          </Card>
        ) : (
          <Card className="p-12 text-center" data-testid="no-questions-message">
            <Calendar className="h-16 w-16 text-[#CBD5E0] mx-auto mb-4" />
            <h3 className="text-xl font-serif text-[#14213D] mb-2">No Questions Available</h3>
            <p className="text-[#5A6577] mb-6">
              No daily current events questions have been created for {new Date(selectedDate).toLocaleDateString()}
            </p>
            {selectedDate === new Date().toISOString().split('T')[0] && (
              <Button
                className="btn-primary"
                onClick={() => setShowCreate(true)}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Create Questions for Today
              </Button>
            )}
          </Card>
        )}
      </div>
    </AppShell>
  );
}
